import { Badge, Card, Group, Stack, Text, Title } from "@mantine/core";
import { useMemo } from "react";

import { useGetSurveys } from "../../../api/insights";
import { ResponseBreakdown } from "../charts";
import SectionShell, {
  usePartShown,
  EmptyState,
  Stat,
  StatGrid,
  formatNum,
  formatPct,
} from "../SectionShell";

const QUESTION_TYPE_LABEL = {
  multiple_choice: "Multiple choice",
  single_choice: "Single choice",
  text: "Text",
};

const QuestionBreakdown = ({ question }) => (
  <ResponseBreakdown
    type={question.questionType === "text" ? "text" : "choice"}
    options={question.options}
    answers={question.textAnswers}
  />
);

const QuestionBlock = ({ question }) => (
  <Card withBorder padding="sm" radius="md" bg="white">
    <Stack gap={6}>
      <Group gap="xs" wrap="wrap" align="center">
        <Badge variant="light" color="blue" size="xs">
          {QUESTION_TYPE_LABEL[question.questionType] ??
            question.questionType ??
            "—"}
        </Badge>
        <Text size="sm" fw={500}>
          {question.questionText ?? "(untitled question)"}
        </Text>
      </Group>
      <QuestionBreakdown question={question} />
    </Stack>
  </Card>
);

function SurveysAnalysis({ eventId }) {
  const { data, isPending, isError, error } = useGetSurveys(eventId);
  const showBreakdown = usePartShown("perSurveyBreakdown");

  const surveys = useMemo(() => data?.surveys ?? [], [data]);

  return (
    <SectionShell
      id="surveysAnalysis"
      title="Surveys"
      description="Post-event survey participation and completion."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="surveysAnalysis"
    >
      <StatGrid>
        <Stat
          part="totalSurveys"
          label="Total surveys"
          value={formatNum(data?.totalSurveys)}
        />
        <Stat
          part="totalResponses"
          label="Total responses"
          value={formatNum(data?.totalResponses)}
        />
        <Stat
          part="participants"
          label="Participants"
          value={formatNum(data?.participants)}
        />
        <Stat
          part="participationRate"
          label="Participation rate"
          value={formatPct(data?.participationRate)}
        />
      </StatGrid>

      {showBreakdown &&
        (surveys.length === 0 ? (
          <EmptyState />
        ) : (
          <Stack gap="md">
            <Title order={5}>Per-survey breakdown</Title>
            {surveys.map((s) => (
              <Card
                key={s.surveyId}
                withBorder
                padding="md"
                radius="md"
                bg="gray.0"
              >
                <Stack gap="sm">
                  <Stack gap={2}>
                    <Text fw={600}>{s.title ?? "(untitled survey)"}</Text>
                    <Text size="xs" c="dimmed">
                      {formatNum(s.starters)} starters ·{" "}
                      {formatNum(s.completers)} completers ·{" "}
                      {formatPct(s.completionRate)} completion ·{" "}
                      {formatNum(s.totalResponses)} responses
                    </Text>
                  </Stack>

                  {(s.questions ?? []).map((q) => (
                    <QuestionBlock key={q.questionId} question={q} />
                  ))}
                </Stack>
              </Card>
            ))}
          </Stack>
        ))}
    </SectionShell>
  );
}

export default SurveysAnalysis;
